"use client";

import { useCallback, useRef, useState } from "react";
import Link from "next/link";
import { LogIn, LogOut, Bookmark, CreditCard, Sparkles } from "lucide-react";
import { useAuthModal } from "@/features/auth/hooks/useAuthModal";
import { usePricingModal } from "@/features/auth/hooks/usePricingModal";
import { useUser } from "@/features/auth/hooks/useUser";
import { useSubscriptionAttention } from "@/features/auth/hooks/useSubscriptionNotice";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { ConfirmLogoutModal } from "@/features/auth/components/ConfirmLogoutModal";
import { isPaidPlan, type UserPlan } from "@/lib/auth/plans";

function planBadge(plan: UserPlan) {
  if (plan === "studio") {
    return (
      <span className="ml-auto text-xs font-medium text-[#a78bfa] bg-[#a78bfa]/10 border border-[#a78bfa]/25 rounded px-1.5 py-0.5">
        Studio
      </span>
    );
  }
  if (isPaidPlan(plan)) {
    return (
      <span className="ml-auto text-xs font-medium text-[#66c0f4] bg-[#66c0f4]/10 border border-[#66c0f4]/20 rounded px-1.5 py-0.5">
        Pro
      </span>
    );
  }
  return (
    <span className="ml-auto text-xs text-[#8f98a0] bg-[#1f2e3f] rounded px-1.5 py-0.5">
      Free
    </span>
  );
}

export function AuthButton() {
  const { open } = useAuthModal();
  const { open: openPricing } = usePricingModal();
  const { user, plan, loading, serverEmail } = useUser();
  const { attention, shortLabel } = useSubscriptionAttention();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const signOutFormRef = useRef<HTMLFormElement>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [confirmLogoutOpen, setConfirmLogoutOpen] = useState(false);

  const closeDropdown = useCallback(() => setDropdownOpen(false), []);
  useOutsideClick(dropdownRef, closeDropdown, dropdownOpen);

  if (loading) {
    return (
      <div className="h-7 w-7 rounded-md bg-[#1f2e3f] border border-[#2a475e]/50 animate-pulse flex-shrink-0" />
    );
  }

  const isAuthenticated = !!user || plan !== "anonymous";

  if (!isAuthenticated) {
    return (
      <button
        type="button"
        onClick={open}
        // `aria-label` always present — the visible "Sign in" label is hidden
        // on mobile (`sm:inline`), and an icon-only button without a label
        // fails the a11y "button-name" check.
        aria-label="Sign in"
        className="inline-flex items-center gap-1.5 text-[13px] font-medium px-2.5 py-1.5 rounded-md border border-[#2a475e]/70 bg-[#1b2838]/80 text-[#c7d5e0] hover:bg-[#1f2e3f] hover:text-white hover:border-[#3a556e] transition-colors flex-shrink-0"
      >
        <LogIn className="w-3.5 h-3.5" aria-hidden="true" />
        <span className="hidden sm:inline">Sign in</span>
      </button>
    );
  }

  const email = user?.email ?? serverEmail ?? "—";
  const initial = email !== "—" ? email[0].toUpperCase() : "?";

  return (
    <div ref={dropdownRef} className="relative flex-shrink-0">
      <button
        type="button"
        onClick={() => setDropdownOpen((o) => !o)}
        className="flex items-center justify-center w-7 h-7 rounded-full border border-[#2a475e]/70 bg-[#2a475e] text-[#c7d5e0] hover:bg-[#3a556e] hover:text-white hover:border-[#66c0f4]/40 transition-colors focus:outline-none focus:ring-2 focus:ring-[#66c0f4]/30 text-xs font-medium"
        title="Account"
        aria-label="Account menu"
        aria-expanded={dropdownOpen}
        aria-haspopup="true"
      >
        {initial}
        {/* Пасивний індикатор стану підписки (past_due / догорає / щойно
            закінчилась) — лишається навіть після dismiss банера. */}
        {attention && (
          <span
            className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-[#e35e1c] ring-2 ring-[#1b2838]"
            aria-hidden="true"
          />
        )}
      </button>

      {dropdownOpen && (
        <div
          className="absolute right-0 top-full mt-2 w-[240px] rounded-lg border border-[#2a475e] bg-[#1b2838] shadow-2xl z-50 overflow-hidden animate-slide-in"
          role="menu"
        >
          <div className="px-3 py-3 border-b border-[#2a475e]/60">
            <p className="text-sm text-[#c7d5e0] truncate" title={email}>
              {email}
            </p>
          </div>
          <div className="p-1.5 space-y-0.5">
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setDropdownOpen(false);
                openPricing("account_menu");
              }}
              className="flex w-full items-center justify-between rounded-md px-2.5 py-2 text-sm text-[#c7d5e0] hover:bg-[#2a475e]/50 hover:text-white transition-colors"
            >
              <span>Plan</span>
              <span className="ml-auto flex items-center gap-1.5">
                {shortLabel && (
                  <span className="text-[11px] text-[#e35e1c]">{shortLabel}</span>
                )}
                {planBadge(plan)}
              </span>
            </button>
            <Link
              href="/account"
              role="menuitem"
              onClick={() => setDropdownOpen(false)}
              className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm text-[#c7d5e0] hover:bg-[#2a475e]/50 hover:text-white transition-colors"
            >
              <CreditCard className="w-4 h-4 shrink-0" />
              Account &amp; billing
            </Link>
            <Link
              href="/watchlist"
              role="menuitem"
              onClick={() => setDropdownOpen(false)}
              className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm text-[#c7d5e0] hover:bg-[#2a475e]/50 hover:text-white transition-colors"
            >
              <Bookmark className="w-4 h-4 shrink-0" />
              Watchlist
            </Link>
            {!isPaidPlan(plan) && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setDropdownOpen(false);
                  openPricing("account_menu_upgrade");
                }}
                className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm font-medium text-[#66c0f4] hover:bg-[#66c0f4]/10 transition-colors"
              >
                <Sparkles className="w-4 h-4 shrink-0" />
                Upgrade
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setDropdownOpen(false);
                setConfirmLogoutOpen(true);
              }}
              className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm text-[#c7d5e0] hover:bg-[#2a475e]/50 hover:text-white transition-colors"
            >
              <LogOut className="w-4 h-4 shrink-0" />
              Sign out
            </button>
          </div>
        </div>
      )}

      <ConfirmLogoutModal
        isOpen={confirmLogoutOpen}
        onClose={() => setConfirmLogoutOpen(false)}
        onConfirm={() => {
          signOutFormRef.current?.requestSubmit();
        }}
      />
      <form
        ref={signOutFormRef}
        action="/api/auth/sign-out"
        method="post"
        className="hidden"
      />
    </div>
  );
}
